<template>
  <div
    class="user-profile-dropdown"
    :class="{ 'is-open': isOpen, collapsed: isCollapsed }"
  >
    <button
      type="button"
      class="user-profile-header"
      :aria-expanded="isOpen"
      aria-haspopup="menu"
      :aria-label="headerAriaLabel"
      :data-tooltip="isCollapsed ? userName : ''"
      @click="toggleDropdown"
    >
      <div class="user-avatar" :class="{ 'user-avatar--has-image': hasProfileImage }">
        <img
          v-if="hasProfileImage"
          :src="profileImageUrl"
          :alt="userName || 'Profile'"
          class="user-avatar-img"
          @error="onImageError"
        />
        <span v-else class="user-initials">{{ userInitials }}</span>
      </div>
      <div v-if="!isCollapsed" class="user-info">
        <span class="user-name">{{ userName }}</span>
        <div v-if="companyName" class="company-info">
          <span class="company-name">{{ companyName }}</span>
        </div>
      </div>
      <span
        v-if="!isCollapsed"
        class="dropdown-arrow"
        :class="{ rotated: isOpen }"
        aria-hidden="true"
      >
        <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
          <path
            d="M1 1.5L6 6.5L11 1.5"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </span>
    </button>

    <Transition name="upd-menu">
      <div
        v-if="isOpen"
        class="dropdown-menu-responsive"
        role="menu"
        :aria-label="isCollapsed ? 'Account menu' : undefined"
      >
      <button
        v-if="showSettings"
        type="button"
        role="menuitem"
        class="menu-item-responsive settings-item"
        :title="isCollapsed ? settingsLabel : undefined"
        @click="handleMenuItemClick({ action: 'settings' })"
      >
        <i class="bi bi-person-gear" aria-hidden="true"></i>
        <span v-if="!isCollapsed">{{ settingsLabel }}</span>
      </button>
      <button
        type="button"
        role="menuitem"
        class="menu-item-responsive logout-item"
        :title="isCollapsed ? 'Logout' : undefined"
        @click="handleMenuItemClick({ action: 'logout' })"
      >
        <i class="bi bi-box-arrow-right" aria-hidden="true"></i>
        <span v-if="!isCollapsed">Logout</span>
      </button>
      </div>
    </Transition>
  </div>
</template>

<script>
import './UserProfileDropdown.css';

export default {
  name: 'UserProfileDropdown',
  props: {
    userName: {
      type: String,
      required: true
    },
    companyName: {
      type: String,
      default: ''
    },
    profileImageUrl: {
      type: String,
      default: ''
    },
    isCollapsed: {
      type: Boolean,
      default: false
    },
    showSettings: {
      type: Boolean,
      default: true
    },
    settingsLabel: {
      type: String,
      default: 'Personal Settings'
    }
  },
  emits: ['menu-item-click', 'dropdown-toggle'],
  data() {
    return {
      isOpen: false,
      imageBroken: false
    };
  },
  computed: {
    hasProfileImage() {
      return (
        !this.imageBroken
        && typeof this.profileImageUrl === 'string'
        && this.profileImageUrl.trim().length > 0
      );
    },
    userInitials() {
      if (!this.userName || this.userName === 'User') return 'U';

      const words = this.userName.trim().split(/\s+/).filter((w) => w.length > 0);
      if (words.length >= 2) {
        const first = words[0].charAt(0).toUpperCase();
        const last = words[words.length - 1].charAt(0).toUpperCase();
        if (first && last) return first + last;
      }
      if (words.length === 1 && words[0].length >= 2) {
        return words[0].substring(0, 2).toUpperCase();
      }
      if (words.length === 1 && words[0].length === 1) {
        return words[0].toUpperCase() + words[0].toUpperCase();
      }
      return 'U';
    },
    headerAriaLabel() {
      const parts = [this.userName];
      if (this.companyName) parts.push(this.companyName);
      parts.push(this.isOpen ? 'Close account menu' : 'Open account menu');
      return parts.join(', ');
    }
  },
  watch: {
    profileImageUrl() {
      this.imageBroken = false;
    },
    isCollapsed(collapsed) {
      if (collapsed) {
        this.closeDropdown();
      }
    }
  },
  mounted() {
    this._onDocumentClick = (event) => {
      if (!this.$el.contains(event.target)) {
        this.closeDropdown();
      }
    };
    document.addEventListener('click', this._onDocumentClick);
  },
  beforeUnmount() {
    if (this._onDocumentClick) {
      document.removeEventListener('click', this._onDocumentClick);
    }
  },
  methods: {
    toggleDropdown() {
      this.isOpen = !this.isOpen;
      this.$emit('dropdown-toggle', this.isOpen);
    },
    handleMenuItemClick(item) {
      this.$emit('menu-item-click', item);
      this.closeDropdown();
    },
    closeDropdown() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.$emit('dropdown-toggle', false);
    },
    onImageError() {
      this.imageBroken = true;
    }
  }
};
</script>
